import { useMemo, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";

import { Box, Typography, Button } from "@mui/material";

import Link from "../../atoms/mui/Link";

import SurveyLayout from "../survey-shape/SurveyLayout";
import { SurveyContext } from "../survey-shape/SurveyContext";

import BomSurveyForm from "./BomSurveyForm";
import BomSurveyScoutPanel from "./BomSurveyScoutPanel";
import BomSurveyCapturedSummary from "./BomSurveyCapturedSummary";
import { readBomProgress } from "./helpers";

import {
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useCollectionDataListener
} from "../../../helpers/firebase";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";

const BACK_HREF = "/explorer/dashboard?view=products";

const AlreadyConfirmedScreen = ({ buyerName }) => (
  <Box
    style={{ height: "100vh", width: "100%" }}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    p={4}
  >
    <Typography variant="h5" align="center" color="textPrimary">
      This BOM request is already confirmed
    </Typography>
    <Box mt={1}>
      <Typography variant="body2" align="center" color="textSecondary">
        {buyerName
          ? `You already shared this BOM with ${buyerName}.`
          : "You already shared this BOM with your customer."}
      </Typography>
    </Box>
    <Box mt={3}>
      <Link href={BACK_HREF}>
        <Button variant="contained" color="primary">
          Back to Dashboard
        </Button>
      </Link>
    </Box>
  </Box>
);

const ExplorerBomSurvey = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lifecycleEventId = params.get("lifecycleEventId");

  const vendorId =
    typeof window !== "undefined"
      ? window.sessionStorage.getItem("accountId")
      : null;

  const [lifecycleEvents, lifecycleEventsLoading] = useCachedDisplayData(
    "vendor-assigned-product-lifecycle-events"
  );
  const [products, productsLoading] = useCachedDisplayData(
    "vendor-assigned-products"
  );
  const [customers] = useCollectionDataListener("customers");

  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { activateSnackbar } = useExplorerLayoutContext() || {};
  const { canEdit } = usePermissions();

  const [scoutConversationId, setScoutConversationId] = useState(null);
  const [isScoutCollapsed, setIsScoutCollapsed] = useState(false);

  const lifecycleEvent = useMemo(
    () =>
      lifecycleEventId
        ? (lifecycleEvents || []).find(({ id }) => id === lifecycleEventId)
        : null,
    [lifecycleEvents, lifecycleEventId]
  );

  const productDoc = useMemo(() => {
    if (!lifecycleEvent) {
      return null;
    }
    return (products || []).find(({ id }) => id === lifecycleEvent.productId);
  }, [products, lifecycleEvent]);

  const buyerName = useMemo(() => {
    if (!lifecycleEvent) {
      return null;
    }
    const customer = (customers || []).find(
      ({ buyerId }) => buyerId === lifecycleEvent.ownerId
    );
    return customer?.buyerName || lifecycleEvent.ownerName || null;
  }, [customers, lifecycleEvent]);

  const surveyLoading = lifecycleEventsLoading || productsLoading;

  if (!lifecycleEventId) {
    return <Redirect to={BACK_HREF} />;
  }

  if (!surveyLoading && !lifecycleEvent) {
    return <Redirect to={BACK_HREF} />;
  }

  if (lifecycleEvent?.bomStatus === "confirmed") {
    return <AlreadyConfirmedScreen buyerName={buyerName} />;
  }

  const productName =
    productDoc?.name || lifecycleEvent?.productName || "this product";
  const productId = lifecycleEvent?.productId;

  const FIELD_ROUTING = {
    productWeight: { collection: "vendor-assigned-products", docId: productId, docField: "productWeight" },
    subcategory: { collection: "vendor-assigned-products", docId: productId, docField: "subcategory" },
    components: { collection: "vendor-assigned-products", docId: productId, docField: "materials" },
    electronics: { collection: "vendor-assigned-products", docId: productId, docField: "electronics" },
    packagingWeight: { collection: "vendor-assigned-product-lifecycle-events", docId: lifecycleEventId, docField: "packagingWeight" },
    packaging: { collection: "vendor-assigned-product-lifecycle-events", docId: lifecycleEventId, docField: "packaging" },
    route: { collection: "vendor-assigned-product-lifecycle-events", docId: lifecycleEventId, docField: "route" }
  };

  const setBomField = async (field, value) => {
    if (!canEdit) {
      return { error: "unauthorized" };
    }
    const routing = FIELD_ROUTING[field];
    if (!routing || !routing.docId) {
      return { error: "missing-target-doc" };
    }
    try {
      await updateCollectionDoc(routing.collection, routing.docId, {
        [routing.docField]: value
      });
      return { success: true };
    } catch (err) {
      console.error("[bom-survey/setBomField] write failed", { field, err });
      activateSnackbar?.({
        severity: "error",
        message:
          "Couldn't save that just now. Check your connection and try again."
      });
      return { error: err };
    }
  };

  const currentBomValues = {
    ...productDoc,
    components: productDoc?.materials || [],
    packagingWeight: lifecycleEvent?.packagingWeight,
    packaging: lifecycleEvent?.packaging || [],
    route: lifecycleEvent?.route || {}
  };
  const { percentComplete } = readBomProgress(currentBomValues);

  const contextValue = {
    vendorId,
    lifecycleEvent,
    productDoc,
    productId,
    productName,
    buyerName,
    lifecycleEventId,
    currentBomValues,
    setBomField,
    readOnly: !canEdit,
    scoutConversationId,
    setScoutConversationId,
    isScoutCollapsed,
    setIsScoutCollapsed,
    backToDashboardHref: BACK_HREF
  };

  return (
    <SurveyContext.Provider value={contextValue}>
      <SurveyLayout
        surveyLoading={surveyLoading}
        percentCompleted={percentComplete}
        form={<BomSurveyForm />}
        summaryPane={<BomSurveyCapturedSummary />}
        mobileSummaryPaneBuilder={({ onCloseMobile }) => (
          <BomSurveyCapturedSummary
            isMobileDrawer
            onCloseMobile={onCloseMobile}
          />
        )}
        scoutPanel={<BomSurveyScoutPanel />}
        backHref={BACK_HREF}
        backLabel="Dashboard"
      />
    </SurveyContext.Provider>
  );
};

export default ExplorerBomSurvey;
